import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ListItemIcon,
  Menu,
  ListItemText,
  IconButton,
  Card,
  TablePagination,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Store as StoreIcon,
  MoreVert as MoreVertIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
  Category,
  ExhibitorDetails,
  ExhibitorFilterParams,
  TableColumn,
} from '../../types';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { BorderedBox } from '../../styles/globalStyle';
import DataTable from '../../components/dataTable';
import { toast } from 'react-toastify';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Close as CloseIcon } from '@mui/icons-material';

const Exhibitors: React.FC = () => {
  const [exhibitors, setExhibitors] = useState<ExhibitorDetails[]>([]);
  const [category, setCategory] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [boothNumber, setBoothNumber] = useState('');
  const [boothType, setBoothType] = useState<'shell_space' | 'raw_space'>(
    'shell_space'
  );
  const [approveError, setApproveError] = useState('');

  const [boothNumberError, setBoothNumberError] = useState('');
  const [boothTypeError, setBoothTypeError] = useState('');

  const [isEditMode, setIsEditMode] = useState(false);

  const handleEditBooth = (exhibitor: ExhibitorDetails) => {
    setSelectedEvent(exhibitor);
    setBoothNumber(exhibitor.boothNumber || '');
    setBoothType(
      (exhibitor.boothType as 'shell_space' | 'raw_space') || 'shell_space'
    );
    setIsEditMode(true);
    setApproveDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const resetApproveForm = () => {
    setBoothNumber('');
    setBoothType('shell_space'); // initial value
    setBoothNumberError('');
    setBoothTypeError('');
    setApproveError('');
  };

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedEvent, setSelectedEvent] = useState<ExhibitorDetails | null>(
    null
  );

  // Filters
  const [filters, setFilters] = useState<ExhibitorFilterParams>({
    page: 1,
    limit: 25,
    searchTerm: '',
    isActive: undefined,
    categoryId: [],
    isVerified: undefined,
  });

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exhibitorToDelete, setExhibitorToDelete] =
    useState<ExhibitorDetails | null>(null);
  useState<ExhibitorDetails | null>(null);

  const fetchCategories = async () => {
    try {
      const categoryResponse = await apiService.getAllCategory();
      setCategory(categoryResponse || []);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchExhibitors = async () => {
    setLoading(true);
    try {
      const exhibitorsResponse = await apiService.getExhibitors(filters);

      setExhibitors(exhibitorsResponse.data || []);
      setTotal(exhibitorsResponse.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch exhibitors');
    } finally {
      setLoading(false);
    }
  };

  // load category only once
  useEffect(() => {
    fetchCategories();
  }, []);

  // load exhibitors when filters change
  useEffect(() => {
    fetchExhibitors();
  }, [filters]);

  const navigate = useNavigate();

  const handleViewExhibitor = (exhibitor: ExhibitorDetails) => {
    navigate(`/exhibitors/${exhibitor.id}`);
  };

  const handleDeleteExhibitor = (exhibitor: ExhibitorDetails) => {
    setExhibitorToDelete(exhibitor);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!exhibitorToDelete) return;

    try {
      await apiService.deleteExhibitor(exhibitorToDelete.id);
      setDeleteDialogOpen(false);
      setExhibitorToDelete(null);
      fetchExhibitors();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete exhibitor');
    }
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    eventItem: ExhibitorDetails
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedEvent(eventItem);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedEvent(null);
  };

  const handleToggleStatus = async (user: ExhibitorDetails) => {
    if (!user?.user?.id) {
      setError('User ID not found');
      return;
    }
    try {
      const message = selectedEvent?.user?.isActive
        ? `Exhibitor has been deactivated`
        : `Exhibitor has been activated`;

      await apiService.toggleUserStatus(user.user.id);
      toast.success(message);

      fetchExhibitors();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to toggle Exhibitor status'
      );
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.exportExhibitors(filters);
      const data = response.data || [];

      // Convert to CSV
      const csvContent = [
        Object.keys(data[0] || {}).join(','),
        ...data.map(item => Object.values(item).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exhibitors_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export exhibitors');
    }
  };

  const handleApproveExhibitor = async () => {
    let hasError = false;

    if (!boothNumber.trim()) {
      setBoothNumberError('Booth number is required');
      hasError = true;
    }

    if (!boothType) {
      setBoothTypeError('Booth type is required');
      hasError = true;
    }

    if (hasError || !selectedEvent) return;

    try {
      setLoading(true);

      if (isEditMode) {
        await apiService.updateExhibitorBooth(selectedEvent.id, {
          boothNumber,
          boothType,
        });

        toast.success('Booth details updated successfully');
      } else {
        await apiService.approveExhibitor(selectedEvent.id, {
          boothNumber,
          boothType,
        });

        toast.success('Exhibitor approved successfully');
      }
      setApproveDialogOpen(false);
      setIsEditMode(false);
      resetApproveForm();
      fetchExhibitors();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setApproveDialogOpen(false);
      setIsEditMode(false);
      resetApproveForm();
    }
  };
  const column: TableColumn<ExhibitorDetails>[] = [
    { label: 'ID', key: 'id' },
    { label: 'Company Name', key: 'companyName' },
    { label: 'Email', key: 'user.email' },
    { label: 'Country', key: 'country' },
    { label: 'Representative', key: 'representativeName' },
    { label: 'Booth', key: 'boothNumber' },
    {
      label: 'Booth Type',
      key: 'boothType',
      renderGroup: values => {
        const [boothType] = values as [string];
        const formatBoothType = (val: string) => {
          if (!val) return '';

          return val
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
        };
        return <BorderedBox>{formatBoothType(boothType)}</BorderedBox>;
      },
    },
    { label: 'Status', key: 'user.isActive', chip: 'status' },
    {
      label: 'Verified',
      key: 'isVerified',
      chip: 'status',
      booleanLabels: { true: 'Yes', false: 'No' },
    },
    { label: 'Leads', key: 'leads' },
  ];

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">Exhibitors Management</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters */}
        {/* Filters */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {/* LEFT FILTERS */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                flex: 1,
              }}
            >
              {/* Search */}
              <TextField
                label="Search Exhibitor"
                size="small"
                value={filters.searchTerm}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    searchTerm: e.target.value,
                    page: 1,
                  }))
                }
                sx={{ minWidth: 220 }}
              />

              {/* Status */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  label="Status"
                  onChange={e => {
                    const value = e.target.value;
                    setFilters(prev => ({
                      ...prev,
                      isActive: value === '' ? undefined : value === 'true',
                      page: 1,
                    }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              {/* Category */}
              <Autocomplete
                multiple
                options={category.filter(cat => cat.is_active)}
                getOptionLabel={option => option.name}
                value={category.filter(cat =>
                  filters.categoryId?.includes(cat.id)
                )}
                onChange={(_, selected) => {
                  setFilters(prev => ({
                    ...prev,
                    categoryId: selected.map(cat => cat.id),
                    page: 1,
                  }));
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: 260 }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Category"
                    placeholder="Select categories"
                  />
                )}
              />
            </Box>

            {/* RIGHT FILTER (VERIFIED TOGGLE) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: 0.5,
              }}
            >
              <Button
                size="small"
                variant={
                  filters.isVerified === undefined ? 'contained' : 'text'
                }
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    isVerified: undefined,
                    page: 1,
                  }))
                }
                sx={{ textTransform: 'none' }}
              >
                All
              </Button>

              <Button
                size="small"
                variant={filters.isVerified === true ? 'contained' : 'text'}
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    isVerified: true,
                    page: 1,
                  }))
                }
                sx={{ textTransform: 'none' }}
              >
                Verified
              </Button>

              <Button
                size="small"
                variant={filters.isVerified === false ? 'contained' : 'text'}
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    isVerified: false,
                    page: 1,
                  }))
                }
                sx={{ textTransform: 'none' }}
              >
                Not Verified
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<ExhibitorDetails>
          columns={column}
          data={exhibitors}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewExhibitor(row),
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => handleDeleteExhibitor(row),
            },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />
        {/* Pagination */}
        {exhibitors.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        {!selectedEvent?.isVerified && !selectedEvent?.boothNumber && (
          <MenuItem
            onClick={() => {
              setIsEditMode(false);
              resetApproveForm();
              setApproveDialogOpen(true);
              setActionMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
        )}
        {selectedEvent?.boothNumber && (
          <MenuItem onClick={() => handleEditBooth(selectedEvent)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Booth</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => selectedEvent && handleToggleStatus(selectedEvent)}
        >
          <ListItemIcon>
            {selectedEvent?.isActive ? (
              <ToggleOffIcon fontSize="small" />
            ) : (
              <ToggleOnIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedEvent?.user?.isActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => selectedEvent && handleDeleteExhibitor(selectedEvent)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete exhibitor "
            {exhibitorToDelete?.fasciaName ||
              exhibitorToDelete?.representativeName}
            "? This action cannot be undone and will also remove all associated
            leads.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, position: 'relative' }}>
          {/* Approve Exhibitor */}
          {isEditMode ? 'Edit Booth Details' : 'Approve Exhibitor'}

          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {approveError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Booth Number"
              required
              fullWidth
              value={boothNumber}
              error={Boolean(boothNumberError)}
              helperText={boothNumberError}
              onChange={e => {
                setBoothNumber(e.target.value);
                if (boothNumberError) setBoothNumberError('');
              }}
              placeholder="Enter booth number"
            />

            <FormControl fullWidth required error={Boolean(boothTypeError)}>
              <InputLabel>Booth Type</InputLabel>
              <Select
                label="Booth Type"
                value={boothType}
                onChange={e => {
                  setBoothType(e.target.value as 'shell_space' | 'raw_space');
                  if (boothTypeError) setBoothTypeError('');
                }}
              >
                <MenuItem value="shell_space">Shell Space</MenuItem>
                <MenuItem value="raw_space">Raw Space</MenuItem>
              </Select>

              {boothTypeError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {boothTypeError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApproveExhibitor}
          >
            {isEditMode ? 'Update' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Exhibitors;
